import { SourceType } from '@atlas/types'
import { useNavigate } from 'react-router-dom'
import type { WorkOrder } from '@/domain/work-order.types'
import { useWithThumbnails } from '@/hooks/useThumbnails'
import { downloadFile, useDeleteWorkOrderFile } from '@/hooks/useWorkOrderFiles'
import { sourceOwner, useUploadSource, useWorkOrderSources } from '@/hooks/useWorkOrderSources'
import { DeliverableCard } from '../WorkOrders/DeliverableCard'
import { useSlotUploadDriver } from '../WorkOrders/workspace/sections/useSlotUploadDriver'
import { CappedUploadDropzone } from './CappedUploadDropzone'

const PACKAGE = SourceType.ORIGINAL_DISTRIBUTION_PACKAGE

/**
 * The client's distribution package files uploaded outside Veeva: source material the order
 * is made from, the counterpart of its original Veeva codes, never one of its deliverables.
 */
export const useDistributionPackageFiles = (orderCode: string) => useWorkOrderSources(orderCode, PACKAGE).data ?? []

/**
 * The distribution package files uploaded outside Veeva, and, for whoever may add to the
 * package, the drop zone to upload them and a delete on each.
 */
export function DistributionPackageUploads({ workOrder: wo, canEdit }: { workOrder: WorkOrder; canEdit: boolean }) {
	const files = useWithThumbnails(useDistributionPackageFiles(wo.orderCode))
	const navigate = useNavigate()
	const upload = useUploadSource(wo.workOrderId, wo.orderCode, wo.tuple, PACKAGE)
	const del = useDeleteWorkOrderFile(wo.orderCode)
	const { rows, cardProps, uploadAll } = useSlotUploadDriver(
		sourceOwner(wo.orderCode, PACKAGE),
		upload.mutateAsync,
		files,
	)

	return (
		<div className="space-y-2">
			{canEdit ? (
				<CappedUploadDropzone label="Upload distribution package files" onFiles={(list) => void uploadAll(list)} />
			) : null}
			{rows.length ? (
				<ul className="space-y-2">
					{rows.map((row) => (
						<DeliverableCard
							key={row.key}
							{...cardProps(row)}
							onOpen={(id) => void navigate(`/assets/${id}`)}
							onDownload={(id) => void downloadFile(id)}
							onDelete={canEdit ? (id) => del.mutate(id) : undefined}
						/>
					))}
				</ul>
			) : null}
		</div>
	)
}
